import axios, { AxiosResponse } from "axios"
import { UUIDType } from "uuid"
import {
  activoAmortizacionesAdapter,
  activoByIdAdapter,
  activoDetalleAmortizacionByIdAdapter,
  activosByFiltersAdapter,
} from "./Activo.adapter"
import { ImportarActivosExcelCommandProps } from "./Commands/ImportarActivosExcelCommand"
import { UpdateActivoCommandProps } from "./Commands/UpdateActivoCommand"
import { GetActivoByIdDtoProps } from "./DTOs/GetActivoByIdDto"
import { GetActivosByFiltersDtoProps } from "./DTOs/GetActivosByFiltersDto"
import { GetAmortizacionesDtoPropsProps } from "./DTOs/GetAmortizacionesDto"
import { GetDetalleAmortizacionActivoByIdDtoProps } from "./DTOs/GetDetalleAmortizacionActivoByIdDto"
import { GetActivosByFiltersDtoIPagedEnumerableProps } from "./Schemas/GetActivosByFiltersDtoIPagedEnumerable"
import { GetAmortizacionesDtoIPagedEnumerableProps } from "./Schemas/GetAmortizacionesDtoIPagedEnumerable"
import { PeriodoType } from "./Types/Periodo"
import { TipoAmortizacionActivoType } from "./Types/TipoAmortizacionActivo"
import { SortingCriteria } from "./interfaces/SortingCriteria"

export interface getActivosByFiltersParamsProps {
  Estados?: number[]
  FechaLowerLimit?: string
  FechaUpperLimit?: string
  Keyword?: string
  SortingCriteria?: SortingCriteria | string
  IsEnabled?: boolean
  MaxResultCount?: number
  SkipCount?: number
}
/**
 *
 * Retrieves a list of activos based on the provided filters.
 *
 * @param queryParams - The parameters used to filter the activos.
 * @returns A Promise that resolves to an object containing the paginated list of activos.
 *
 */
export async function getActivosByFilters(
  queryParams: getActivosByFiltersParamsProps
): Promise<GetActivosByFiltersDtoIPagedEnumerableProps> {
  const { data } = await axios.get("/Activo", {
    params: queryParams,
  })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((activo: GetActivosByFiltersDtoProps) =>
      activosByFiltersAdapter(activo)
    ),
    totalCount: data.totalCount,
  }
}

export const getActivosExportarExcel = (queryParams: {
  FechaLowerLimit: string
  FechaUpperLimit: string
}) => {
  return axios.get("/Activo/exportar-excel", {
    params: queryParams,
    responseType: "blob",
  })
}

export interface CreateActivoRequestBodyProps {
  cuentaContableContrapartidaId?: string
  cuentaContableId?: string
  empresaId?: string
  esAmortizable?: boolean
  fechaAdquisicion?: string
  importe?: number
  importePendiente?: number
  importeValorResidual?: number
  nombre?: string
  tipoInmovilizado?: number
}
/**
 *
 * Creates a new Activo.
 *
 * @param requestBody - The request body containing the properties of the Activo to be created.
 * @returns A Promise that resolves to the response from the server.
 *
 */
export async function createActivo(requestBody: CreateActivoRequestBodyProps) {
  const response = await axios.post("/Activo", requestBody)
  return response
}

/**
 *
 * Retrieves an activo by its ID.
 * @param id - The ID of the activo to retrieve.
 * @returns A Promise that resolves to a GetActivoByIdDtoProps object representing the retrieved activo.
 *
 */
export async function getActivoById(
  id: string
): Promise<GetActivoByIdDtoProps> {
  const { data } = await axios.get(`/Activo/${id}`)
  return activoByIdAdapter(data)
}

/**
 * Retrieves the detailed amortization information of an asset by its ID.
 * @param id - The ID of the asset.
 * @returns A promise that resolves to the detailed amortization information of the asset.
 */
export async function GetDetalleAmortizacionActivoById(
  id: string
): Promise<GetDetalleAmortizacionActivoByIdDtoProps> {
  const { data } = await axios.get(`/Activo/${id}/informacion-amortizacion`)
  return activoDetalleAmortizacionByIdAdapter(data)
}

/**
 * Updates an Activo.
 *
 * @param requestBody - The properties of the Activo to be updated.
 * @returns A Promise that resolves to the AxiosResponse containing the updated Activo.
 */
export async function updateActivo(
  requestBody: UpdateActivoCommandProps
): Promise<AxiosResponse> {
  const response = await axios.put(`/Activo/${requestBody.id}`, requestBody)
  return response
}

export interface PostAndPutActivoAmortizacionInfoRequestBodyProps {
  id?: string
  anyos?: number
  coeficiente?: number
  coeficienteAmortizacionId?: number
  cuentaContableAcumuladaId?: string
  cuentaContableGastoId?: string
  tipo?: TipoAmortizacionActivoType
  tipoPeriodo?: PeriodoType
}
/**
 * Posts the amortization information for an asset.
 * @param requestBody - The request body containing the amortization information.
 * @returns A promise that resolves to the Axios response.
 */
export async function postActivoAmortizacionInfo(
  requestBody: PostAndPutActivoAmortizacionInfoRequestBodyProps
): Promise<AxiosResponse> {
  const response = await axios.post(
    `/Activo/${requestBody?.id}/informacion-amortizacion`,
    requestBody
  )
  return response
}

export async function putActivoAmortizacionInfo(
  requestBody: PostAndPutActivoAmortizacionInfoRequestBodyProps
): Promise<AxiosResponse> {
  const response = await axios.put(
    `/Activo/${requestBody?.id}/informacion-amortizacion`,
    requestBody
  )
  return response
}

export async function getActivoAmortizaciones(
  id: string
): Promise<GetAmortizacionesDtoIPagedEnumerableProps> {
  const { data } = await axios.get(`/Activo/${id}/amortizaciones`)
  return {
    currentPage: data?.currentPage,
    items: data?.items?.map((amortizacion: GetAmortizacionesDtoPropsProps) =>
      activoAmortizacionesAdapter(amortizacion)
    ),
    totalCount: data?.totalCount,
  }
}

export interface postActivoAmortizaciones {
  id?: UUIDType | string
  amortizacionId?: UUIDType | string
}

export async function postActivoAmortizacionContabilizar(
  queryParams: postActivoAmortizaciones
): Promise<void> {
  await axios.post(`/Activo/${queryParams.id}/amortizacion/${queryParams.amortizacionId}/contabilizar
`)
}

export async function postActivoAmortizacionDescontabilizar(
  queryParams: postActivoAmortizaciones
): Promise<void> {
  await axios.post(`/Activo/${queryParams.id}/amortizacion/${queryParams.amortizacionId}/descontabilizar
`)
}

export async function deleteActivo(id: string) {
  const resp = await axios.delete(`/Activo/${id}`)
  return resp
}

export async function importarExcelActivos(
  requestBody: ImportarActivosExcelCommandProps
) {
  const resp = await axios.post("/Activo/importar-excel", requestBody)
  return resp
}
